// WHAT: Layout-Komponente für logisch gruppierte Dashboard-Bereiche mit optionalem Collapsing
// WHY:  Strukturierte Organisation von Widget-Gruppen (z. B. Core Metrics, Health Metrics) im Core-Dashboard
// DOMAIN: core → dashboard → layout
// DEPENDENCIES: TracQ `Heading`, `cn` Utility, `DashboardGrid` als Container für Widgets
// RISKS:
//   - NOTE: Collapsing-State ist lokal und nicht persistent; Seiten-Reload setzt den Zustand zurück

import { ChevronDown, ChevronUp } from "lucide-react";
import { type FC, type ReactNode, useState } from "react";

import { Heading } from "@/components/ui";
import { cn } from "@/shared/utils";

import { DashboardGrid } from "./DashboardGrid";

interface DashboardSectionProperties {
  children: ReactNode;
  className?: string;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  description?: string;
  gridClassName?: string;
  id: string;
  title: string;
}

/**
 * WHAT:
 *   Kapselt eine beschreibbare Dashboard-Sektion mit Titel, optionaler Beschreibung und optionalem
 *   Collapsing-Toggle, die ihre Kinder in einem `DashboardGrid` rendert.
 *
 * WHY:
 *   Erhöht die Lesbarkeit und Navigierbarkeit komplexer Dashboards, indem verwandte Widgets in klar
 *   benannten Abschnitten zusammengefasst werden.
 *
 * PROPS:
 *   - id: string — DOM-ID und Basis für das `aria-labelledby`-Label.
 *   - title: string — Abschnittstitel (Heading-Level 2).
 *   - description?: string — Optionaler kurzer Beschreibungstext unterhalb des Titels.
 *   - children: ReactNode — Enthaltene Widgets/Komponenten.
 *   - collapsible?: boolean — Aktiviert/Deaktiviert den Collapse-Toggle.
 *   - defaultCollapsed?: boolean — Initialer Zustand (eingeklappt/ausgeklappt).
 *   - className?: string — Zusätzliche Klassen für das Section-Element.
 *   - gridClassName?: string — Zusätzliche Klassen für das `DashboardGrid`.
 *
 * BEHAVIOR:
 *   - Verwaltet `isCollapsed` lokal per `useState`.
 *   - Setzt ARIA-Attribute (`aria-labelledby`, `aria-expanded`) für bessere Zugänglichkeit.
 *
 * RISKS:
 *   - NOTE: Persistente User-Präferenzen (welche Sektion eingeklappt ist) müssten in einem globalen Store abgelegt werden.
 *
 * @param root0
 *
 * @param root0.id
 *
 * @param root0.title
 *
 * @param root0.description
 *
 * @param root0.children
 *
 * @param root0.collapsible
 *
 * @param root0.defaultCollapsed
 *
 * @param root0.className
 *
 * @param root0.gridClassName
 *
 * @returns Dashboard section component
 *
 */
export const DashboardSection: FC<DashboardSectionProperties> = ({
  children,
  className,
  collapsible = false,
  defaultCollapsed = false,
  description,
  gridClassName,
  id,
  title,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  return (
    <section aria-labelledby={`section-${id}`} className={cn("w-full", className)} id={id}>
      {/* Section Header - Compact, toggle button close to title */}
      <div className="mb-4 flex items-start justify-between gap-3 w-full">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Heading
              className="text-xs uppercase tracking-widest text-slate-400 pl-1"
              id={`section-${id}`}
              level={2}
            >
              {title}
            </Heading>
            {collapsible ? (
              <button
                aria-expanded={!isCollapsed}
                aria-label={isCollapsed ? "Expand section" : "Collapse section"}
                className={cn(
                  "p-1.5 rounded-md transition-colors flex-shrink-0",
                  "text-gray-400 hover:text-gray-200 hover:bg-gray-700/50",
                  "focus:outline-none focus:ring-2 focus:ring-tracq-bright-blue focus:ring-offset-2 focus:ring-offset-gray-900",
                )}
                onClick={() => {
                  setIsCollapsed(!isCollapsed);
                }}
              >
                {isCollapsed ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronUp className="w-4 h-4" />
                )}
              </button>
            ) : null}
          </div>
          {description ? <p className="text-sm text-gray-500 mt-1 pl-1">{description}</p> : null}
        </div>
      </div>

      {/* Section Content */}
      {!isCollapsed && (
        <div className="w-full">
          <DashboardGrid {...(gridClassName ? { className: gridClassName } : {})}>
            {children}
          </DashboardGrid>
        </div>
      )}
    </section>
  );
};
